import {useCallback, useEffect, useState} from "react";
import {useCheckoutDocument} from "pay/useCheckoutDocument";
import {useUser} from "UserProvider";
import {acceptanceVersion} from "./acceptedTermsVersion";
import {PUBLISHED_TERMS_VERSION} from "./publishedTermsVersion";
import TermsStorage from "./TermsStorage";

export interface TermsGate {
	/** Whether the consent prompt stands in front of the sender panel, blocking it (ui#1549). */
	open: boolean;
	/** Re-checks this identity's current acceptance at an action boundary. */
	confirmAccepted: () => boolean;
	/**
	 * Records the click-through, with the Terms version the api announces at the click (or, when the
	 * lookup gives none, the version this bundle publishes), for the signed-in account, if any; the verified
	 * Firebase identity claims it in the replay funnel. The click is held until the announcement answers.
	 */
	accept: () => void;
}

/**
 * React adapter for account-scoped terms state. It starts closed for SSG/hydration parity, then mirrors
 * the store after mount. Same-tab store notifications cover acceptance, account claims and silent
 * server-rejection discard, so the gate cannot stay closed over a record the API refused.
 *
 * At the click it asks the api which Terms it announces for this reader, through the checkout document
 * reader, and records that version, or the bundle's own when the lookup gives none (kitecorp/transferify-ui#3485). Asked at the click, not when the prompt
 * appears: the prompt is mounted on pages where nobody is asked to agree (the recipient page's reply
 * composer), and a visitor who never clicks costs no request. What a click records is
 * `./acceptedTermsVersion`'s rule; this hook only holds a click until the rule says to record it.
 */
export function useTermsGate(): TermsGate {
	const [user] = useUser();
	const accountId = user.isLoggedIn ? user.id : null;
	const [open, setOpen] = useState(false);
	// A click held for the announcement: when the person agreed, and as which account.
	const [heldClick, setHeldClick] = useState<{at: Date; by: string | null} | null>(null);
	const announcement = useCheckoutDocument(heldClick !== null);
	const decision = acceptanceVersion(announcement.status, announcement.document, PUBLISHED_TERMS_VERSION);
	const confirmAccepted = useCallback(() => {
		const accepted = TermsStorage.retrieve(accountId);
		setOpen(!accepted);
		return accepted;
	}, [accountId]);
	useEffect(() => {
		confirmAccepted();
		return TermsStorage.subscribe(confirmAccepted);
	}, [confirmAccepted]);
	const record = useCallback((version: string, clickedAt: Date, clickedBy: string | null) => {
		TermsStorage.save(clickedBy, version, clickedAt);
		setOpen(false);
	}, []);
	const recordable = decision.kind === "record";
	const version = decision.kind === "record" ? decision.version : PUBLISHED_TERMS_VERSION;
	const accept = useCallback(() => {
		const clickedAt = new Date();
		setHeldClick(held => held ?? {at: clickedAt, by: accountId});
	}, [accountId]);
	useEffect(() => {
		if (heldClick === null) {
			return;
		}
		// Dropped, not recorded, when the prompt closed some other way or another account is now signed
		// in: the click belongs to the account that made it, and the prompt it answered is gone.
		if (!open || heldClick.by !== accountId) {
			setHeldClick(null);
		} else if (recordable) {
			setHeldClick(null);
			record(version, heldClick.at, heldClick.by);
		}
	}, [heldClick, open, accountId, recordable, version, record]);
	return {open, confirmAccepted, accept};
}
